import { LanguageService } from "app/core/services/language/language.service";
import { Component, HostListener, OnDestroy, OnInit } from "@angular/core";
import { RoleService } from "app/core/services/role/role.service";
import { AuthService } from "app/auth/services/auth.service";
import { NavigationEnd, Router } from "@angular/router";
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from "@angular/common";
import { filter, Subscription } from "rxjs";
import { User } from "app/core/models/user";

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [ CommonModule, TranslateModule ],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss'
})

export class HeaderComponent implements OnInit, OnDestroy {

	public user: any;

    public isAdmin: boolean = false;

    public selectedRole: string = '';

    public activeRoute: string = '';

	private subscription?: Subscription;

	public isDropdownOpen: boolean = false;

	public languages: { code: string, label: string, flagcode: string }[] = [
		{ code: 'es', label: 'Español', flagcode: 'es' },
		{ code: 'en', label: 'English', flagcode: 'gb' },
		{ code: 'eu', label: 'Euskera', flagcode: 'es-pv' }
	];

    constructor(
		private laguageService: LanguageService,
		private roleService: RoleService,
		private authService: AuthService,
        private router: Router
    ) { }

    ngOnInit(): void { 
		this.obtainProfile();
		this.isAdmin = this.isAdministrator();

		this.router.events
			.pipe(
				filter((event): event is NavigationEnd => event instanceof NavigationEnd)
			)
			.subscribe((event: NavigationEnd) => {
				const url = event.urlAfterRedirects || event.url;
				this.setActiveRoute(url);
			});

		this.subscription = this.roleService.currentRole$.subscribe(role => {
			this.selectedRole = role;
		});
	}

	ngOnDestroy(): void {
		this.subscription?.unsubscribe();
	}

    private async obtainProfile(): Promise<void> {
		try {
			const profile = await this.authService.getUserProfile();
			if (!profile) {
				console.error('No profile returned from getUserProfile()');
				this.user = User.DEFAULT;
				return;
			}
			if (!profile.id && !profile.username) {
				console.error('Profile is missing id and username', profile);
			}
			this.user = new User(
				profile.id ?? User.DEFAULT.ID,
				profile.username ?? User.DEFAULT.username,
				profile.firstName ?? User.DEFAULT.name,
				profile.lastName ?? User.DEFAULT.surname,
				profile.email ?? User.DEFAULT.email,
				profile.emailVerified ?? User.DEFAULT.emailVerified
			);
		} catch (err) {
			console.error('Error getting user profile in obtainProfile()', err);
			this.user = User.DEFAULT;
		}
	}

    private isAdministrator(): boolean {
		return this.authService.checkRealmRolesIncludeAnyOfRequiredRoles(['ds-catalog-administrator']);
	}

    private setActiveRoute(url: string): void {
		const routeMap: Record<string, string> = {
			'/administration': 'administration',
			'/catalog': 'catalog'
		};

		const match = Object.entries(routeMap).find(([path]) => url.includes(path));
		this.activeRoute = match ? match[1] : '';
	}

	public selectRole(role: string): void {
		this.roleService.setRole(role);
	}

    public navigateTo(path: string): void {
		const currentPath = this.router.url.split('/')[1];
		if (currentPath !== path) {
			this.router.navigate(['home', path]);
			this.selectedRole = this.getRoleFromPath(path);
		}
	}

	private getRoleFromPath(path: string): string {
		switch (path) {
			case 'administration':
				return 'Administrator';
			case 'catalog':
				return 'Catalog';
			default:
				return '';
		}
	}

	public toggleDropdown(): void {
		this.isDropdownOpen = !this.isDropdownOpen;
	}

	@HostListener('document:click', ['$event'])
	public closeDropdownOnClickOutside(event: MouseEvent): void {
		const target = event.target as HTMLElement;
		if (!target.closest('.dropdown')) {
			this.isDropdownOpen = false;
		}
	}

	public changeLang(lang: string): void {
		this.laguageService.changeLang(lang);
	}

	public logout(): void {
		this.isDropdownOpen = false;
		this.authService.logout();
	}

}